import * as React from 'jsx-dom'
import Haptic from '@/js/App/Haptic'

type ButtonOperation = 'getDecrement' | 'getIncrement'
type ButtonSize = undefined | 'xs'

export type QuantityInputEvents =
	| 'valueSnapped.quantityInput'
	| 'valueMaxClamped.quantityInput'
	| 'valueMinClamped.quantityInput'

type QuantityInputValueModifications = {
	isSnapped?: boolean
	isMinClamped?: boolean
	isMaxClamped?: boolean
}

type QuantityInputValue = {
	value: number
	modifications: QuantityInputValueModifications
}

export class QuantityInput {
	public static readonly selector = '.js-quantity-input'

	private element: HTMLElement
	private input: HTMLInputElement
	private inputCopy: HTMLSpanElement

	private decrementButton: HTMLButtonElement
	private incrementButton: HTMLButtonElement

	private min: number
	private max: number
	private step: number

	// We want all computations to use integers. To achieve this, we determine the number of decimal places in the step
	// value and multiply all relevant values (min, max, step, and the actual value) by the corresponding factor. This
	// allows us to perform all calculations and comparisons using integers only. When writing the result back to the
	// input, we divide by the same factor to convert it back to a float.
	private factor: number
	private decimalPlaces: number

	private rapidChangeTimer: ReturnType<typeof setTimeout> | null = null

	private readonly startRapidChangeEvents = ['mousedown', 'touchstart'] as const
	private readonly stopRapidChangeEvents = ['mouseup', 'mouseleave', 'touchend', 'touchcancel'] as const

	private rapidChangeFlag: boolean = false
	private valueChangedFlag: boolean = false

	public constructor(element: HTMLElement) {
		const input = element.querySelector<HTMLInputElement>(`${QuantityInput.selector}__input`)
		const wrap = element.querySelector<HTMLElement>(`${QuantityInput.selector}__input-wrap`)

		if (!input || !wrap) {
			throw new Error('QuantityInput: Missing input element.')
		}

		this.element = element
		this.input = input

		const step = parseFloat(input.step) || 1
		this.decimalPlaces = this.getDecimalPlaces(step)
		this.factor = 10 ** this.decimalPlaces

		this.step = Math.round(step * this.factor)
		this.min = Math.round(parseFloat(input.min) * this.factor) || 0
		this.max = Math.round(parseFloat(input.max) * this.factor) || Number.MAX_VALUE

		const size = element.dataset.size as ButtonSize
		this.decrementButton =
			element.querySelector<HTMLButtonElement>(`${QuantityInput.selector}__btn--dec`) || this.getButton('−', size)
		this.incrementButton =
			element.querySelector<HTMLButtonElement>(`${QuantityInput.selector}__btn--inc`) || this.getButton('+', size)

		this.inputCopy = (<span aria-hidden="true" class="sr-only"></span>) as HTMLSpanElement

		this.element.insertAdjacentElement('beforeend', this.decrementButton)
		this.element.insertAdjacentElement('beforeend', this.incrementButton)
		wrap.insertAdjacentElement('beforeend', this.inputCopy)

		this.calculateInputWidth()
		this.attachHandlers()
		this.setButtonsState()
	}

	private attachHandlers(): void {
		this.input.addEventListener('change', this.adjustValueToRange.bind(this))
		this.input.addEventListener('input', this.calculateInputWidth.bind(this))
		this.input.addEventListener('input', this.setButtonsState.bind(this))
		this.input.addEventListener('focus', () => {
			this.input.select()
		})
		this.input.addEventListener('blur', () => {
			if (isNaN(this.input.valueAsNumber)) {
				this.setValue(this.min)
			}
		})
		;[this.decrementButton, this.incrementButton].forEach((button) => {
			button.addEventListener('click', this.handleClick.bind(this))

			// a long tap on Android would open a context menu; for iOS it is necessary to use
			// `-webkit-touch-callout: none` in CSS
			button.addEventListener('contextmenu', (event) => event.preventDefault())

			this.startRapidChangeEvents.forEach((eventName) => {
				button.addEventListener(eventName, this.startRapidChange.bind(this), { passive: true })
			})
			this.stopRapidChangeEvents.forEach((eventName) => {
				button.addEventListener(eventName, this.stopRapidChange.bind(this), { passive: true })
			})
		})
	}

	private getButton(text: string, size: ButtonSize): HTMLButtonElement {
		return (
			<button
				type="button"
				aria-hidden="true"
				tabIndex={-1}
				class={['form-quantity__btn btn btn--outline', size ? `btn--${size}` : null]}
			>
				{text}
			</button>
		) as HTMLButtonElement
	}

	private calculateInputWidth(): void {
		this.inputCopy.innerHTML = this.input.value
		// +2 is for "rendering safety margin"
		this.input.style.width = `${this.inputCopy.scrollWidth + 2}px`
	}

	private getValue(): number {
		const value = this.input.valueAsNumber

		return isNaN(value) ? this.min : Math.round(this.input.valueAsNumber * this.factor)
	}

	private setValue(
		value: number,
		modifications: QuantityInputValueModifications = {},
		emitEvent: boolean = true
	): void {
		this.input.value = value === 0 ? '0' : (value / this.factor).toFixed(this.decimalPlaces)

		if (emitEvent) {
			this.dispatchEvents(modifications)
		}

		this.calculateInputWidth()
		this.setButtonsState()
	}

	private dispatchEvents(modifications: QuantityInputValueModifications): void {
		this.input.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { isTrigger: true } }))
		this.input.dispatchEvent(new CustomEvent('validate', { bubbles: true }))

		if (modifications.isSnapped) {
			this.input.dispatchEvent(new CustomEvent('valueSnapped.quantityInput', { bubbles: true }))
		}

		if (modifications.isMaxClamped) {
			this.input.dispatchEvent(new CustomEvent('valueMaxClamped.quantityInput', { bubbles: true }))
		}

		if (modifications.isMinClamped) {
			this.input.dispatchEvent(new CustomEvent('valueMinClamped.quantityInput', { bubbles: true }))
		}
	}

	private clampAndSnapValue(value: number): QuantityInputValue {
		const clampedAndSnappedValue: QuantityInputValue = {
			value: value,
			modifications: {}
		}

		const snappedValue = Math.ceil(value / this.step) * this.step

		if (value !== snappedValue) {
			clampedAndSnappedValue.value = snappedValue
			clampedAndSnappedValue.modifications.isSnapped = true
		}

		if (snappedValue > this.max) {
			clampedAndSnappedValue.value = this.max
			clampedAndSnappedValue.modifications.isMaxClamped = true
		}

		if (snappedValue < this.min) {
			clampedAndSnappedValue.value = this.min
			clampedAndSnappedValue.modifications.isMinClamped = true
		}

		return clampedAndSnappedValue
	}

	private adjustValueToRange(event: Event | CustomEvent): void {
		if ('detail' in event && event.detail.isTrigger) {
			return
		}

		const value = this.getValue()
		const newValue = this.clampAndSnapValue(value)

		if (value !== newValue.value) {
			event.stopImmediatePropagation()
			this.setValue(newValue.value, newValue.modifications)
		}
	}

	private setButtonsState(): void {
		const value = this.getValue()

		this.decrementButton.disabled = value <= this.min
		this.incrementButton.disabled = value >= this.max
	}

	private getButtonOperation(event: Event): ButtonOperation {
		return event.currentTarget === this.decrementButton ? 'getDecrement' : 'getIncrement'
	}

	private getIncrement(value: number): number {
		value += this.step

		return Math.min(value, this.max)
	}

	private getDecrement(value: number): number {
		value -= this.step

		return Math.max(value, this.min)
	}

	private handleClick(event: MouseEvent): void {
		event.preventDefault()

		if (!this.rapidChangeFlag && !(event.target as HTMLButtonElement).disabled) {
			let value = this.getValue()

			value = this[this.getButtonOperation(event)](value)
			this.setValue(value)
			Haptic.vibrate(Haptic.VIBRATE_DEFAULT)
		}

		this.rapidChangeFlag = false
	}

	private startRapidChange(event: MouseEvent | TouchEvent): void {
		let value = this.getValue()
		const operation = this.getButtonOperation(event)
		let counter = 0

		const rapidChange = () => {
			counter++
			const newValue = this[operation](value)

			this.rapidChangeFlag = true

			if (value !== newValue) {
				value = newValue

				this.valueChangedFlag = true
				this.setValue(value, {}, false)

				Haptic.vibrate(Haptic.VIBRATE_DEFAULT)

				if (counter === 1) {
					this.setButtonsState()
				}
			} else {
				// Min / max value achieved as value didn't change → we don't need to wait for mouse release
				if (this.valueChangedFlag) {
					this.stopRapidChange()
				}
				return
			}

			let delay = 150
			if (counter > 10) {
				delay = Math.max(10, delay - 5 * counter)
			}
			this.rapidChangeTimer = setTimeout(rapidChange, delay)
		}

		this.rapidChangeTimer = setTimeout(rapidChange, 500)
	}

	private stopRapidChange() {
		this.rapidChangeFlag = false
		Haptic.stopVibrate()

		if (this.rapidChangeTimer) {
			clearTimeout(this.rapidChangeTimer)
			this.rapidChangeTimer = null
		}

		if (this.valueChangedFlag) {
			this.input.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { isTrigger: true } }))
			this.valueChangedFlag = false
		}
	}

	private getDecimalPlaces(number: number): number {
		if (!isFinite(number)) {
			return 0
		}

		const numberString = number.toString()
		const dotIndex = numberString.indexOf('.')

		if (dotIndex === -1) {
			return 0
		}

		return Math.min(10, numberString.slice(dotIndex + 1).replace(/0+$/, '').length)
	}
}

declare global {
	interface HTMLElementEventMap {
		'valueSnapped.quantityInput': CustomEvent
		'valueMaxClamped.quantityInput': CustomEvent
		'valueMinClamped.quantityInput': CustomEvent
	}

	interface DocumentEventMap {
		'valueSnapped.quantityInput': CustomEvent
		'valueMaxClamped.quantityInput': CustomEvent
		'valueMinClamped.quantityInput': CustomEvent
	}
}
